"use client";

import { useMemo, memo } from "react";
import DynamicWidget from "@/components/DynamicWidget";
import BetaBadge from "@/components/widgets/BetaBadge";
import { useWidgetPreview } from "@/hooks/useWidgetPreview";

// Lightweight widget wrapper for view mode only
// This bypasses all the editing controls and complex state from EditableWidget
const ViewWidget = memo(function ViewWidget({
  id,
  layout,
  config,
  widget,
  spaceId,
  spaceSlug,
  spaceData,
  previewMode = "desktop",
  handleWidgetUpdate,
}) {
  // Memoize widget styles for view mode - much simpler than EditableWidget
  const widgetStyles = useMemo(() => {
    const {
      padding,
      backgroundColor,
      borderColor,
      borderWidth,
      borderRadius,
      transparency,
    } = layout.settings || {};

    // Calculate background color with transparency
    let bgColor = backgroundColor || "transparent";
    if (
      backgroundColor &&
      transparency !== undefined &&
      widget.widget_type.type !== "single_media"
    ) {
      // Convert hex to rgba
      const r = parseInt(backgroundColor.slice(1, 3), 16);
      const g = parseInt(backgroundColor.slice(3, 5), 16);
      const b = parseInt(backgroundColor.slice(5, 7), 16);
      bgColor = `rgba(${r}, ${g}, ${b}, ${transparency / 100})`;
    }

    // Special handling for SingleMediaWidget
    if (widget.widget_type.type === "single_media") {
      return {
        width: "100%",
        height: "100%",
        padding: 0,
        backgroundColor: "transparent",
        border: "none",
        borderRadius: borderRadius || "1.5rem",
        position: "relative",
      };
    }

    // Special handling for VideoPlayerWidget - always no padding
    if (widget.widget_type.type === "video_player") {
      return {
        width: "100%",
        height: "100%",
        padding: 0,
        backgroundColor: bgColor,
        border: borderColor
          ? `${borderWidth || "1px"} solid ${borderColor}`
          : "none",
        borderRadius: borderRadius || "1.5rem",
        position: "relative",
      };
    }

    // Special handling for MapWidget - edge-to-edge like single_media
    if (widget.widget_type.type === "map") {
      return {
        width: "100%",
        height: "100%",
        padding: 0,
        backgroundColor: "transparent",
        border: "none",
        borderRadius: borderRadius || "1.5rem",
        position: "relative",
      };
    }

    if (
      widget.widget_type.type === "spotify" ||
      widget.widget_type.type === "apple_music"
    ) {
      return {
        width: "100%",
        height: "100%",
        padding: 0,
        backgroundColor: bgColor,
        border: borderColor
          ? `${borderWidth || "1px"} solid ${borderColor}`
          : "none",
        borderRadius: borderRadius || "1.5rem",
        position: "relative",
      };
    }

    // Special handling for ImageGalleryWidget - respect padding when there's a background
    if (widget.widget_type.type === "image_gallery") {
      const hasBackground =
        backgroundColor &&
        backgroundColor !== "transparent" &&
        !layout.settings?.backgroundColorGlobal;

      return {
        width: "100%",
        height: "100%",
        padding: hasBackground ? padding || "1rem" : 0,
        backgroundColor: bgColor,
        border: borderColor
          ? `${borderWidth || "1px"} solid ${borderColor}`
          : "none",
        borderRadius: borderRadius || "1.5rem",
        position: "relative",
      };
    }

    // Normal styling for other widgets
    return {
      width: "100%",
      height: "100%",
      padding,
      backgroundColor: bgColor,
      border: borderColor
        ? `${borderWidth || "1px"} solid ${borderColor}`
        : "none",
      borderRadius: borderRadius || "1.5rem",
      position: "relative",
    };
  }, [layout.settings, widget.widget_type.type]);

  // Use the preview hook to get effective settings with real-time preview
  const effectiveSettings = useWidgetPreview(id, layout?.settings || {});

  // Memoize the widget props
  const widgetProps = useMemo(
    () => ({
      widget,
      layout: {
        ...layout,
        settings: effectiveSettings,
      },
      config,
      settings: effectiveSettings,
      onConfigUpdate: () => {}, // No-op in view mode
      handleWidgetUpdate: handleWidgetUpdate || (() => {}), // No-op fallback
      isEditing: false, // Always false in view mode
      onDelete: () => {}, // No-op in view mode
      debugModeEnabled: false, // No debug in view mode
      spaceData,
      spaceSlug,
      widgetId: id,
      previewMode,
    }),
    [
      widget,
      layout,
      config,
      effectiveSettings,
      handleWidgetUpdate,
      spaceData,
      spaceSlug,
      id,
      previewMode,
    ]
  );

  return (
    <div
      data-widget-id={id}
      data-edge-to-edge={
        widget.widget_type.type === "single_media" ||
        widget.widget_type.type === "image_gallery" ||
        widget.widget_type.type === "map" ||
        (widget.widget_type.type === "video_player" &&
          (!layout.settings?.backgroundColor ||
            layout.settings?.backgroundColor === "transparent" ||
            layout.settings?.backgroundColorGlobal))
      }
      className="relative min-w-0 h-full"
      style={widgetStyles}
    >
      {/* Beta badge - shows in view mode */}
      {widget.widget_type?.is_beta &&
        spaceData?.space_data?.show_beta_tags !== false && <BetaBadge />}

      <div className="w-full h-full">
        <DynamicWidget {...widgetProps} />
      </div>
    </div>
  );
});

ViewWidget.displayName = "ViewWidget";

export default ViewWidget;
